import useDeleteTag from "api/api-hooks/tags/useDeleteTag";
import useDeleteTagGroup from "api/api-hooks/tags/useDeleteTagGroup";
import useTags from "api/api-hooks/tags/useTags";
import useUpdateTagGroup from "api/api-hooks/tags/useUpdateTagGroup";
import AppPageHeader from "components/Headers/AppPageHeader";
import DotsOptionsMenu from "components/Menus/DotsOptionsMenu";
import DropdownOptionWithIcon from "components/Menus/DropdownOptionWithIcon";
import PopoverDropdownMenu from "components/Menus/PopoverDropdownMenu";
import ColorModal from "components/Modals/ColorModal";
import SingleInputModal from "components/Modals/SingleInputModal";
import IconButton from "elements/IconButton";
import SkeletonLoader from "elements/SkeletonLoader";
import useToggle from "hooks/useToggle";
import pluralize from "pluralize";
import { Fragment, useState, useMemo } from "react";
import { Col, Row } from "reactstrap";
import { AppConfirm } from "utils/PopupUtil";
import AppSpinner from "../../elements/AppSpinner";
import AppTag from "./AppTag";
import SaveTagDrawer from "./SaveTagDrawer";

const Options = ({
  onOpenCreate,
  onOpenEditName,
  onOpenEditColor,
  onDeleteGroup,
}) => (
  <PopoverDropdownMenu>
    <DropdownOptionWithIcon
      icon="mdi mdi-plus-box"
      onClick={onOpenCreate}
      name="Create Tag In Group"
    />
    <DropdownOptionWithIcon
      icon="mdi mdi-pencil"
      onClick={onOpenEditName}
      name="Update Group Name"
    />
    <DropdownOptionWithIcon
      icon="mdi mdi-palette"
      onClick={onOpenEditColor}
      name="Update Group Color"
    />
    <DropdownOptionWithIcon
      icon="mdi mdi-delete"
      onClick={onDeleteGroup}
      name="Delete Group"
    />
  </PopoverDropdownMenu>
);

const ManageTags = ({ title, scope, description, headerSize = "" }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null); // Used for creating a new tag in a group
  const [selectedGroupName, setSelectedGroupName] = useState(null); // Used for editing the name of a group
  const [selectedGroupColor, setSelectedGroupColor] = useState(null); // Used for editing the color of a group
  const [groupNameModal, toggleGroupNameModal] = useToggle();
  const [groupColorModal, toggleGroupColorModal] = useToggle();
  const { updateTagGroup, updatingGroup, updateGroupError } =
    useUpdateTagGroup();
  const { deleteTag, deletingTag, deletingID } = useDeleteTag();
  const { deleteTagGroup, deletingGroupName } = useDeleteTagGroup();
  const { tagGroups, tagsLoading } = useTags(scope);

  // Gets the Color for each tag group.
  const defaultColors = useMemo(() => {
    const colorMap = {};

    if (tagsLoading || !tagGroups) return colorMap;

    Object.keys(tagGroups).forEach((group) => {
      const groupColor = tagGroups[group][0].Color;
      colorMap[group] = groupColor;
    });

    return colorMap;
  }, [tagsLoading, tagGroups]);

  const onOpen = (tag = null, group = null) => {
    if (tag) setSelectedTag(tag);
    if (group) setSelectedGroup(group);
    setDrawerOpen(true);
  };

  const onClose = () => {
    setSelectedTag(null);
    setSelectedGroup(null);
    setDrawerOpen(false);
  };

  // Handles delete tags, and tags group. Pass groupName to delete a tags group.
  const onDelete = async (tag, groupName = null) => {
    const result = await AppConfirm({
      title: `Confirm Tag ${groupName ? "Group" : ""} Deletion`,
      html: `Are you sure you want to delete this tag${
        groupName ? " group? All tags inside this group will be deleted." : "?"
      }`,
    });

    if (!result.isConfirmed) return;

    if (groupName) {
      deleteTagGroup({
        scope: scope,
        groupName: groupName,
      });
    } else {
      deleteTag({
        scope: scope,
        tagID: tag.id,
      });
    }
  };

  const handleGroupNameUpdate = (newName, onSuccess) => {
    const payload = { TagGroup: newName };

    updateTagGroup({
      groupName: selectedGroupName,
      payload: payload,
      scope: scope,
      onSuccess: onSuccess,
    });
  };

  const handleGroupColorUpdate = (newColor, onSuccess) => {
    const payload = { Color: newColor };

    updateTagGroup({
      groupName: selectedGroupColor,
      payload: payload,
      scope: scope,
      onSuccess: onSuccess,
    });
  };

  const DisplayTags = () => {
    if (!tagGroups || typeof tagGroups !== "object") {
      return (
        <div>
          <div className="p-3 bg-ultralight border border-lighter rounded">
            <Row className="align-items-center">
              <Col xs="auto" className="pr-0">
                <h1 className="m-0">
                  <i className="mdi mdi-tag"></i>
                </h1>
              </Col>
              <Col xs="">
                <h4 className="mb-0 font-weight-normal">
                  Get started by adding your first tag.
                </h4>
              </Col>
            </Row>
          </div>
        </div>
      );
    }

    let groups = [];
    let noGroup;

    for (const group of Object.keys(tagGroups)) {
      const data = { title: group };

      const tags = tagGroups[group];
      const displayTags =
        tags && tags.length > 0 ? (
          tags.map((t, i) => (
            <Fragment key={t.id}>
              {i === 0 && <hr className="mb-2 mt-0" />}
              <div className="d-flex justify-content-between align-items-center px-3 py-1">
                <AppTag tag={t} />
                <div className="d-flex">
                  <IconButton
                    className="mr-1"
                    onClick={() => onOpen(t)}
                    icon="pencil"
                  />
                  <IconButton
                    onClick={() => onDelete(t)}
                    icon="close"
                    color="danger"
                    loading={deletingTag && deletingID === t.id}
                  />
                </div>
              </div>
              <hr className={i === tags?.length - 1 ? "mt-2 mb-0" : "my-2"} />
            </Fragment>
          ))
        ) : (
          <>No tags</>
        );

      data.group = displayTags;

      if (group === "No Group") {
        noGroup = data;
      } else {
        groups.push(data);
      }
    }

    groups = groups.sort((a, b) => (a.title > b.title ? 1 : -1));

    return (
      <>
        {groups.map((group, i) => (
          <div
            key={i}
            className="mb-4 outline rounded border border-lighter shadow-sm"
          >
            <div
              className="d-flex justify-content-between align-items-center px-3"
              style={{ height: 45 }}
            >
              {deletingGroupName === group.title ? (
                <div className="d-flex">
                  <h4 className="mb-0 mr-2">Deleting Group...</h4>
                  <AppSpinner size="sm" />
                </div>
              ) : (
                <h4 className="mb-0">{group.title}</h4>
              )}
              {!deletingGroupName && (
                <DotsOptionsMenu
                  dotsSize={24}
                  placement="auto"
                  options={
                    <Options
                      onOpenCreate={() => onOpen(null, group.title)}
                      onOpenEditName={() => {
                        toggleGroupNameModal();
                        setSelectedGroupName(group.title);
                      }}
                      onOpenEditColor={() => {
                        toggleGroupColorModal();
                        setSelectedGroupColor(group.title);
                      }}
                      onDeleteGroup={() => onDelete(null, group.title)}
                    />
                  }
                />
              )}
            </div>
            {group.group}
          </div>
        ))}
        {noGroup && (
          <div className="mb-4 outline rounded border">
            <h4
              className="mb-0 px-3 d-flex align-items-center"
              style={{ height: 45 }}
            >
              No Group
            </h4>
            {noGroup.group}
          </div>
        )}
      </>
    );
  };

  if (tagsLoading) {
    return (
      <>
        <SkeletonLoader />
        <SkeletonLoader />
        <SkeletonLoader />
        <SkeletonLoader />
      </>
    );
  }

  return (
    <>
      <AppPageHeader
        title={title}
        description={description}
        action={() => onOpen(null)}
        actionButtonName="Add"
        size={headerSize}
      />
      {headerSize === "sm" && <hr className="my-3"></hr>}
      <DisplayTags />
      <SaveTagDrawer
        open={drawerOpen}
        onClose={onClose}
        scope={scope}
        groupName={selectedGroup}
        existingTag={selectedTag}
        color={defaultColors?.[selectedGroup]}
      />
      <SingleInputModal
        open={groupNameModal}
        onClose={toggleGroupNameModal}
        title="Edit Group Name"
        confirmButtonText="Save"
        onConfirm={handleGroupNameUpdate}
        submitting={updatingGroup}
        error={updateGroupError}
        existingValue={selectedGroupName}
      />
      <ColorModal
        title={
          selectedGroupColor && pluralize(selectedGroupColor) + " Tag Color"
        }
        subTitle="Change the color for every tag in this group"
        open={groupColorModal}
        onClose={toggleGroupColorModal}
        saving={updatingGroup}
        onSave={handleGroupColorUpdate}
      />
    </>
  );
};

export default ManageTags;
